/**
 * SiteMapLayersPanel — Reader Polish Prompt 4.
 *
 * Inline layer toggle UI for the SiteLandingPage Overview map.
 * Replaces the legacy "32 imported layers — open the report for the
 * full toggle UI" caption with a real, scrollable, per-layer + per-
 * category control surface mounted as an overlay on the map.
 *
 * Visual: bottom-left anchor by default, ≤60% of map height with
 * internal scroll, collapsible per-category groups, per-layer rows.
 *
 * State model: this component is purely controlled. Parents own
 *   - `hiddenCategories`  — full categories that are off.
 *   - `hiddenLayerIds`    — individual layer ids that are off.
 *
 * A layer is shown iff its id is NOT in `hiddenLayerIds` AND its
 * category is NOT in `hiddenCategories` AND `layer.visible !== false`.
 */
import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Crosshair, ExternalLink, Eye, EyeOff, Locate, Search, X } from 'lucide-react';
import { LAYER_COLORS, CATEGORY_LABELS, CATEGORY_ORDER, SOURCE_BADGES, colorForCategory } from '../../config/siteLayerColors';
import { isManagedGenerationImpactLayer } from '../../lib/siteLayers/managedGenerationImpactLayers';
import type { SiteLayer, SiteLayerCategory } from '../../types/site';
import {
  categoryKeyLabel,
  isCustomCategoryKey,
  layerCategoryKey,
} from './generationImpact/studio/studioLayers';

/**
 * A parcel row for the "Parcels" group. Display-ready (name already
 * disambiguated, color already resolved by the caller) so the panel stays
 * purely presentational and free of geometry/palette logic.
 */
export interface ParcelPanelEntry {
  id: string;
  name: string;
  /** Swatch/outline color — matches the parcel's drawn outline one-for-one. */
  color: string;
}

export interface SiteMapLayersPanelProps {
  layers: SiteLayer[];
  /** Category keys hidden by the viewer. Keys are `SiteLayerCategory`
   *  values OR a user-defined `SiteLayer.customCategory` label. */
  hiddenCategories: ReadonlySet<string>;
  hiddenLayerIds: ReadonlySet<string>;
  onToggleCategory: (cat: string) => void;
  onToggleLayer: (id: string) => void;
  onReset: () => void;
  /** Position anchor on the map. Defaults to 'bottom-left' per spec. */
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  /**
   * MP (multi-parcel) — the site's parcels, listed as a toggleable "Parcels"
   * group ABOVE the category-grouped layers. Disjoint KMZ parcels live in
   * `site.siteBoundaries[]` (NOT `siteLayers[]`), so without this they'd
   * never appear in the panel. Optional + defaults to none, so every
   * existing (layers-only) caller is unaffected.
   */
  parcels?: ReadonlyArray<ParcelPanelEntry>;
  /** Set of currently-hidden parcel ids (viewer-only, owned by the parent). */
  hiddenParcelIds?: ReadonlySet<string>;
  /** Toggle a single parcel's visibility. Required when `parcels` is passed. */
  onToggleParcel?: (id: string) => void;
  /** Geometry kinds hidden by the viewer ('polygon' | 'line' | 'point'). */
  hiddenGeometryTypes?: ReadonlySet<SiteLayer['geometry']['type']>;
  /**
   * Toggle a geometry kind on/off (type-filter chips above the groups).
   * Optional ⇒ chips hidden. Chips also hide when the site's layers span
   * fewer than two geometry kinds — a one-kind filter can only blank the map.
   */
  onToggleGeometryType?: (type: SiteLayer['geometry']['type']) => void;
  /** Pan/zoom the map to a layer's geometry. Optional ⇒ rows hide the control. */
  onZoomToLayer?: (layer: SiteLayer) => void;
  /**
   * Solo a category (hide every other category; call again to unsolo).
   * Optional ⇒ group headers hide the control.
   */
  onSoloCategory?: (cat: string) => void;
  /**
   * Published Generation & Impact layout metadata — when present, the
   * publication-managed `scenario-derived` layers group under a dedicated
   * "Generation layout" header (scenario name + publish date) instead of
   * scattering across their thematic categories.
   */
  generationHead?: { scenarioName: string; publishedAt: string } | null;
  /** Deep link into the Generation & Impact tab. */
  onOpenGeneration?: () => void;
  /**
   * Draft (promoted-but-unpublished) layout preview toggle. Present only
   * when the site has a promoted scenario whose geometry is not (or no
   * longer) what's published.
   */
  draftLayout?: {
    enabled: boolean;
    onToggle: () => void;
    /** Layer count once computed (enabled), null before. */
    count: number | null;
    /** Geometry precondition message when the preview can't build. */
    error?: string | null;
  } | null;
  /** P4 — per-category fill-opacity overrides (slider values). */
  categoryOpacity?: Readonly<Record<string, number>>;
  /** P4 — set a category's fill opacity (0.05–1). Optional ⇒ sliders hidden. */
  onSetCategoryOpacity?: (cat: string, opacity: number) => void;
  /** P4 — move a layer one step up/down in draw order (within its category).
   *  Optional ⇒ rows hide the reorder controls. */
  onMoveLayer?: (id: string, direction: 'up' | 'down') => void;
}

const POSITION_CLASSES: Record<NonNullable<SiteMapLayersPanelProps['position']>, string> = {
  'top-right':    'top-2 right-2',
  'top-left':     'top-2 left-2',
  'bottom-right': 'bottom-2 right-2',
  'bottom-left':  'bottom-2 left-2',
};

interface CategoryGroup {
  category: string;
  layers: SiteLayer[];
}

/** Chip order + labels for the geometry-type filter row. */
const GEOMETRY_TYPE_CHIPS: ReadonlyArray<{ type: SiteLayer['geometry']['type']; label: string }> = [
  { type: 'polygon', label: 'Polygons' },
  { type: 'line',    label: 'Lines' },
  { type: 'point',   label: 'Points' },
];

/* ── Segment bundling ─────────────────────────────────────────────────────
 *
 * Regional ArcGIS fetches turn one physical asset into many segment
 * features that share a base label and differ only by an appended
 * disambiguator ("115 kV · PacifiCorp · 212144", "… · 212145", "Gas Main
 * · part 2"). Listing each segment as its own row made a 300-segment
 * fetch read as a wall of identical truncated names. A bundle collapses
 * those siblings into ONE row with a segment count and a tri-state
 * toggle; genuinely unique layers stay as plain rows.
 */

export type LayerDisplayRow =
  | { kind: 'single'; layer: SiteLayer }
  | { kind: 'bundle'; key: string; name: string; members: SiteLayer[] };

/** Strip the suffixes our own converters append to sibling segments. */
function strippedName(name: string): { b1: string; candidate: string } {
  const b1 = name
    .replace(/\s+\(\d+\)$/, '')
    .replace(/\s·\spart\s\d+$/, '');
  // A trailing " · <token>" (no spaces in token) is the id-style
  // disambiguator `disambiguateFeatureNames` appends.
  const candidate = b1.replace(/\s·\s[A-Za-z0-9._-]+$/, '');
  return { b1, candidate };
}

export function bundleGroupLayers(layers: SiteLayer[]): LayerDisplayRow[] {
  if (layers.length < 2) {
    return layers.map(layer => ({ kind: 'single' as const, layer }));
  }
  const metas = layers.map(l => strippedName(l.name));
  // Candidates that appear on MULTIPLE layers (or match another layer's
  // full base name) mark a shared asset; one-off candidates are just
  // names that happen to contain a "·".
  const candCount = new Map<string, number>();
  for (const m of metas) {
    if (m.candidate !== m.b1) candCount.set(m.candidate, (candCount.get(m.candidate) ?? 0) + 1);
  }
  const b1Set = new Set(metas.map(m => m.b1));
  const keyOf = (m: { b1: string; candidate: string }): string => {
    if (candCount.has(m.b1)) return m.b1; // parent row of suffixed siblings
    if (m.candidate !== m.b1 && ((candCount.get(m.candidate) ?? 0) >= 2 || b1Set.has(m.candidate))) {
      return m.candidate;
    }
    return m.b1;
  };
  const byKey = new Map<string, SiteLayer[]>();
  layers.forEach((l, i) => {
    const k = keyOf(metas[i]);
    const list = byKey.get(k);
    if (list) list.push(l);
    else byKey.set(k, [l]);
  });
  return [...byKey.entries()].map(([key, members]) =>
    members.length === 1
      ? { kind: 'single' as const, layer: members[0] }
      : { kind: 'bundle' as const, key, name: key, members },
  );
}

function layerMatchesQuery(layer: SiteLayer, q: string): boolean {
  return (
    layer.name.toLowerCase().includes(q)
    || (layer.description?.toLowerCase().includes(q) ?? false)
    || categoryKeyLabel(layerCategoryKey(layer)).toLowerCase().includes(q)
  );
}

function bundleSlug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function layerSourceTitle(layer: SiteLayer): string | undefined {
  return layer.source === 'arcgis-import'
    ? 'Source: ArcGIS'
    : layer.source === 'peeringdb-import'
      ? 'Source: PeeringDB'
      : layer.source === 'kmz-import'
        ? 'Source: KMZ'
        : layer.source === 'doc-derived'
          ? 'Source: Document extraction'
          : layer.source === 'landgate-import'
            ? `Source: LandGate (${layer.evidence?.promotion?.state ?? 'map-only'})`
            : undefined;
}

/** Shared visibility/handler context for LayerRow / BundleRow. */
interface RowContext {
  hiddenLayerIds: ReadonlySet<string>;
  hiddenGeometryTypes?: ReadonlySet<SiteLayer['geometry']['type']>;
  categoryHidden: boolean;
  onToggleLayer: (id: string) => void;
  onZoomToLayer?: (layer: SiteLayer) => void;
}

function LayerRow({
  layer,
  ctx,
  indent = false,
  reorder,
}: {
  layer: SiteLayer;
  ctx: RowContext;
  /** Bundle-member rows indent and drop the source badge (shared with the bundle head). */
  indent?: boolean;
  reorder?: { canUp: boolean; canDown: boolean; onMove: (id: string, direction: 'up' | 'down') => void };
}) {
  const typeHidden = ctx.hiddenGeometryTypes?.has(layer.geometry.type) ?? false;
  const layerHidden = ctx.hiddenLayerIds.has(layer.id) || ctx.categoryHidden || typeHidden || layer.visible === false;
  const dotColor = colorForCategory(layer.category, layer.color);
  return (
    <label
      className={`flex items-center gap-2 py-0.5 cursor-pointer hover:bg-state-hover ${indent ? 'pl-5' : ''}`}
      data-testid={`layers-row-${layer.id}`}
    >
      <input
        type="checkbox"
        checked={!layerHidden}
        disabled={ctx.categoryHidden || typeHidden}
        onChange={() => ctx.onToggleLayer(layer.id)}
        className="w-3 h-3 accent-primary shrink-0"
        data-testid={`layers-toggle-${layer.id}`}
        aria-label={`Toggle layer ${layer.name}`}
      />
      <span
        className="w-2 h-2 inline-block shrink-0 rounded-full"
        style={{ backgroundColor: dotColor }}
        aria-hidden
      />
      <span
        className={`flex-1 text-[10px] truncate ${layerHidden ? 'text-text-muted line-through' : 'text-text-primary'}`}
        title={[layer.name, layer.description, layerSourceTitle(layer)].filter(Boolean).join(' · ')}
      >
        {layer.name}
      </span>
      {!indent && (
        <span
          className="shrink-0 rounded border border-border-subtle px-1 text-[10px] uppercase tracking-wider text-text-muted"
          data-testid={`layers-badge-${layer.id}`}
        >
          {SOURCE_BADGES[layer.source] ?? layer.source}
        </span>
      )}
      {ctx.onZoomToLayer && layer.geometry.coordinates.length > 0 && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); ctx.onZoomToLayer!(layer); }}
          className="text-text-muted hover:text-text-primary shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
          aria-label={`Zoom to ${layer.name}`}
          title="Zoom to this layer"
          data-testid={`layers-zoom-${layer.id}`}
        >
          <Locate className="w-3 h-3" aria-hidden />
        </button>
      )}
      {reorder && (
        <span className="flex flex-col shrink-0 -my-0.5">
          <button
            type="button"
            disabled={!reorder.canUp}
            onClick={(e) => { e.preventDefault(); reorder.onMove(layer.id, 'up'); }}
            className="text-text-muted hover:text-text-primary disabled:opacity-30 focus-visible:outline-none"
            aria-label={`Move ${layer.name} up`}
            title="Move up (drawn beneath later layers)"
            data-testid={`layers-move-up-${layer.id}`}
          >
            <ArrowUp className="w-2.5 h-2.5" aria-hidden />
          </button>
          <button
            type="button"
            disabled={!reorder.canDown}
            onClick={(e) => { e.preventDefault(); reorder.onMove(layer.id, 'down'); }}
            className="text-text-muted hover:text-text-primary disabled:opacity-30 focus-visible:outline-none"
            aria-label={`Move ${layer.name} down`}
            title="Move down (drawn on top of earlier layers)"
            data-testid={`layers-move-down-${layer.id}`}
          >
            <ArrowDown className="w-2.5 h-2.5" aria-hidden />
          </button>
        </span>
      )}
    </label>
  );
}

function BundleRow({
  bundle,
  ctx,
  expanded,
  onToggleExpanded,
}: {
  bundle: Extract<LayerDisplayRow, { kind: 'bundle' }>;
  ctx: RowContext;
  expanded: boolean;
  onToggleExpanded: () => void;
}) {
  const { members, name } = bundle;
  const slug = bundleSlug(name);
  const typeHiddenAll = members.every(m => ctx.hiddenGeometryTypes?.has(m.geometry.type) ?? false);
  // Bundle "on" state is intent-level (the per-layer hide-list only) so the
  // tri-state stays meaningful while a category/type filter is active.
  const onIds = members.filter(m => !ctx.hiddenLayerIds.has(m.id));
  const allOn = onIds.length === members.length;
  const noneOn = onIds.length === 0;
  const bundleHidden = noneOn || ctx.categoryHidden || typeHiddenAll;
  const dotColor = colorForCategory(members[0].category, members[0].color);

  const toggleAll = () => {
    // Any segment on → switch the whole asset off; fully off → restore all.
    const targets = noneOn ? members : onIds;
    for (const m of targets) ctx.onToggleLayer(m.id);
  };

  // Zoom fits the WHOLE asset: hand the zoom handler a synthetic multi-
  // segment geometry so fitBounds covers every member, not just one.
  const zoomAll = ctx.onZoomToLayer
    ? () => ctx.onZoomToLayer!({
        ...members[0],
        geometry: {
          type: 'line',
          coordinates: members.flatMap(m => m.geometry.coordinates),
        },
      })
    : undefined;

  return (
    <div data-testid={`layers-bundle-${slug}`}>
      <label className="flex items-center gap-2 py-0.5 cursor-pointer hover:bg-state-hover">
        <input
          type="checkbox"
          checked={!noneOn}
          ref={el => { if (el) el.indeterminate = !allOn && !noneOn; }}
          disabled={ctx.categoryHidden || typeHiddenAll}
          onChange={toggleAll}
          className="w-3 h-3 accent-primary shrink-0"
          data-testid={`layers-bundle-toggle-${slug}`}
          aria-label={`Toggle all ${members.length} ${name} segments`}
        />
        <span
          className="w-2 h-2 inline-block shrink-0 rounded-full"
          style={{ backgroundColor: dotColor }}
          aria-hidden
        />
        <span
          className={`flex-1 text-[10px] truncate ${bundleHidden ? 'text-text-muted line-through' : 'text-text-primary'}`}
          title={`${name} — ${members.length} segments${allOn ? '' : ` (${onIds.length} visible)`}`}
        >
          {name}
        </span>
        <span
          className="shrink-0 rounded border border-border-subtle px-1 text-[9px] tracking-wider text-text-muted tabular-nums"
          data-testid={`layers-bundle-count-${slug}`}
        >
          {allOn || noneOn ? `×${members.length}` : `${onIds.length}/${members.length}`}
        </span>
        {zoomAll && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); zoomAll(); }}
            className="text-text-muted hover:text-text-primary shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
            aria-label={`Zoom to all ${name} segments`}
            title="Zoom to the whole asset"
            data-testid={`layers-bundle-zoom-${slug}`}
          >
            <Locate className="w-3 h-3" aria-hidden />
          </button>
        )}
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); onToggleExpanded(); }}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${name} segments`}
          className="text-text-muted hover:text-text-primary shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
          data-testid={`layers-bundle-expand-${slug}`}
        >
          {expanded
            ? <ChevronDown className="w-3 h-3" aria-hidden />
            : <ChevronRight className="w-3 h-3" aria-hidden />}
        </button>
      </label>
      {expanded && members.map(m => (
        <LayerRow key={m.id} layer={m} ctx={ctx} indent />
      ))}
    </div>
  );
}

export function SiteMapLayersPanel({
  layers,
  hiddenCategories,
  hiddenLayerIds,
  onToggleCategory,
  onToggleLayer,
  onReset,
  position = 'bottom-left',
  parcels,
  hiddenParcelIds,
  onToggleParcel,
  hiddenGeometryTypes,
  onToggleGeometryType,
  onZoomToLayer,
  onSoloCategory,
  generationHead,
  onOpenGeneration,
  draftLayout,
  categoryOpacity,
  onSetCategoryOpacity,
  onMoveLayer,
}: SiteMapLayersPanelProps) {
  // Top-level panel collapsed state. Starts MINIMISED (header only) so the
  // legend never obscures the map or overlaps the top-left control card; the
  // user expands it on demand. (William QA 2026-06-11.)
  const [panelOpen, setPanelOpen] = useState(false);
  // MP — the "Parcels" group starts EXPANDED (the parcels are the site's
  // primary boundary content) once the user opens the panel.
  const [parcelsExpanded, setParcelsExpanded] = useState(true);
  // Published Generation-layout layers group under their own header (only
  // when the host passes publication metadata); everything else groups by
  // thematic category exactly as before.
  const { managedGenerationLayers, categoryLayers } = useMemo(() => {
    if (!generationHead) return { managedGenerationLayers: [] as SiteLayer[], categoryLayers: layers };
    const managed: SiteLayer[] = [];
    const rest: SiteLayer[] = [];
    for (const l of layers) (isManagedGenerationImpactLayer(l) ? managed : rest).push(l);
    return { managedGenerationLayers: managed, categoryLayers: rest };
  }, [layers, generationHead]);
  const [generationExpanded, setGenerationExpanded] = useState(true);
  // Search scanline — filters every row (layers, bundles, parcels) by name /
  // description / category. Non-empty query force-expands all groups so
  // matches are never hidden behind a collapsed header.
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  // Bundles start collapsed; per-segment control is one chevron away.
  const [expandedBundles, setExpandedBundles] = useState<Set<string>>(new Set());
  const toggleBundleExpanded = (key: string) => {
    setExpandedBundles(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Per-category expanded state. The first two categories with
  // content default to expanded; the rest collapse to keep the
  // initial panel height bounded for sites with many layers.
  const groups: CategoryGroup[] = useMemo(() => {
    const byCategory = new Map<string, SiteLayer[]>();
    for (const l of categoryLayers) {
      const key = layerCategoryKey(l);
      const arr = byCategory.get(key) ?? [];
      arr.push(l);
      byCategory.set(key, arr);
    }
    // Canonical enum order first, then user-defined categories in first-seen
    // order — the same ordering the studio's layer tree uses.
    const enumGroups = CATEGORY_ORDER
      .filter(c => byCategory.has(c))
      .map(c => ({ category: c as string, layers: byCategory.get(c)! }));
    const customGroups = [...byCategory.entries()]
      .filter(([key]) => isCustomCategoryKey(key))
      .map(([key, layers]) => ({ category: key, layers }));
    return [...enumGroups, ...customGroups];
  }, [categoryLayers]);

  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(() => {
    // Default-expand the first two non-empty categories; collapse the rest so
    // dense sites don't ship a wall of text.
    //
    // Seeded from the SAME grouping the panel renders (enum order, then
    // user-defined categories) rather than from CATEGORY_ORDER alone. Walking
    // only the enum meant a site whose layers all sit in user-defined
    // categories matched nothing and opened fully collapsed — the layers were
    // grouped correctly but every group was shut, which reads as "my custom
    // categories aren't there".
    const e = new Set<string>();
    for (const { category } of groups) {
      if (e.size >= 2) break;
      e.add(category);
    }
    return e;
  });

  const allParcels = parcels ?? [];
  const parcelList = q
    ? allParcels.filter(p => p.name.toLowerCase().includes(q))
    : allParcels;
  const hasParcels = parcelList.length > 0;

  // Render the panel when there is anything toggleable — layer groups,
  // parcels, the generation-layout group, or the draft-preview toggle. (A
  // KMZ of pure disjoint parcels has no `siteLayers`, so the legacy
  // `groups.length === 0` guard would have hidden the whole panel.)
  // Mount decision uses the UNfiltered lists — an active search query that
  // matches nothing must never unmount the panel (and the search box with it).
  // (Context streaming toggles moved to the top-left rail's Context menu,
  // 2026-08-18 — SiteMapContextMenu — so they no longer hold the panel open.)
  if (groups.length === 0 && allParcels.length === 0 && managedGenerationLayers.length === 0 && !draftLayout) {
    return null;
  }

  const layerTotal = layers.length;
  const layerVisible = layers.filter(l => {
    if (l.visible === false) return false;
    if (hiddenLayerIds.has(l.id)) return false;
    if (hiddenCategories.has(l.category)) return false;
    if (hiddenGeometryTypes?.has(l.geometry.type)) return false;
    return true;
  }).length;

  // Type-filter chips render only for kinds the site actually has, and only
  // when there are ≥2 distinct kinds (filtering a single-kind site is a no-op
  // that can only blank the map).
  const geometryTypeCounts = useMemo(() => {
    const counts = new Map<SiteLayer['geometry']['type'], number>();
    for (const l of layers) counts.set(l.geometry.type, (counts.get(l.geometry.type) ?? 0) + 1);
    return counts;
  }, [layers]);
  const showTypeChips = onToggleGeometryType !== undefined && geometryTypeCounts.size >= 2;

  const parcelVisible = allParcels.filter(p => !hiddenParcelIds?.has(p.id)).length;

  // Header count stays LAYER-scoped whenever layers exist (so "Layers · X/Y"
  // keeps its exact prior meaning); a parcels-only map (no layers) titles the
  // header "Parcels · X/Y" instead of a misleading "Layers · 0/0". Either way
  // the Parcels group carries its own visible/total count below.
  const headerLabel = layerTotal > 0 || allParcels.length === 0 ? 'Site layers' : 'Parcels';
  const headerVisible = layerTotal > 0 || allParcels.length === 0 ? layerVisible : parcelVisible;
  const headerTotal = layerTotal > 0 || allParcels.length === 0 ? layerTotal : allParcels.length;

  function toggleCategoryExpanded(cat: string) {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  }

  return (
    <div
      className={`map-glass z-10 font-mono flex flex-col ${position === 'bottom-left' ? 'map-legend' : `${POSITION_CLASSES[position]} w-[16rem] max-w-[calc(100%-1.75rem)]`}`}
      style={{ maxHeight: '60%' }}
      data-testid="site-map-layers-panel"
    >
      <button
        type="button"
        onClick={() => setPanelOpen(o => !o)}
        className="flex items-center justify-between px-3 py-2 type-section text-text-primary hover:bg-state-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
        aria-expanded={panelOpen}
        data-testid="layers-panel-toggle"
      >
        <span>{headerLabel} · {headerVisible}/{headerTotal}</span>
        <span className="text-text-muted text-xs">{panelOpen ? '−' : '+'}</span>
      </button>
      {panelOpen && (
        <div className="border-t border-border-subtle overflow-y-auto" data-testid="layers-panel-body">
          {/* Search scanline — the primary tool once fetches push a site past
              a handful of layers. Filters every row; groups force-expand
              while a query is active so no match hides behind a header. */}
          {layerTotal >= 10 && (
            <div
              className="flex items-center gap-1.5 px-2 py-1 border-b border-border-subtle focus-within:bg-state-hover"
              data-testid="layers-search-row"
            >
              <Search className="w-3 h-3 text-text-muted shrink-0" aria-hidden />
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="FILTER LAYERS"
                className="flex-1 min-w-0 bg-transparent py-0.5 text-[10px] uppercase tracking-wider text-text-primary placeholder:text-text-muted/60 focus:outline-none"
                aria-label="Filter layers by name, description, or category"
                data-testid="layers-search-input"
              />
              {q && (
                <>
                  <span className="text-[9px] text-text-muted tabular-nums shrink-0" data-testid="layers-search-count">
                    {layers.filter(l => layerMatchesQuery(l, q)).length} match
                  </span>
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    className="text-text-muted hover:text-text-primary shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    aria-label="Clear layer filter"
                    data-testid="layers-search-clear"
                  >
                    <X className="w-3 h-3" aria-hidden />
                  </button>
                </>
              )}
            </div>
          )}
          {/* Geometry-type filter chips — a second, global dimension on top of
              the category/per-layer hide-lists (intersection semantics: a
              layer shows only when BOTH its category and its kind are on). */}
          {showTypeChips && (
            <div
              className="flex items-center gap-1 px-2 py-1.5 border-b border-border-subtle"
              data-testid="layers-type-chips"
            >
              {GEOMETRY_TYPE_CHIPS.filter(({ type }) => geometryTypeCounts.has(type)).map(({ type, label }) => {
                const hidden = hiddenGeometryTypes?.has(type) ?? false;
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => onToggleGeometryType?.(type)}
                    aria-pressed={!hidden}
                    aria-label={`${hidden ? 'Show' : 'Hide'} ${label.toLowerCase()}`}
                    className={`px-1.5 py-0.5 rounded-sm border text-[9px] uppercase tracking-wider transition-colors ${
                      hidden
                        ? 'border-border-subtle text-text-muted line-through opacity-60 hover:bg-state-hover'
                        : 'border-border-subtle text-text-primary bg-state-hover/50 hover:bg-state-hover'
                    }`}
                    data-testid={`layers-type-chip-${type}`}
                  >
                    {label} · {geometryTypeCounts.get(type)}
                  </button>
                );
              })}
            </div>
          )}
          {/* MP — "Parcels" group (site.siteBoundaries) ABOVE the category
              layers. Each parcel is a leaf toggle; there is no category-level
              "hide all" because parcels aren't a SiteLayerCategory. */}
          {hasParcels && (
            <div
              data-testid="layers-parcels-group"
              className="border-b border-border-subtle last:border-b-0"
            >
              <div className="flex items-center gap-1 px-2 py-1.5 hover:bg-state-hover">
                <button
                  type="button"
                  onClick={() => setParcelsExpanded(e => !e)}
                  aria-expanded={parcelsExpanded}
                  aria-label="Toggle Parcels group"
                  className="text-text-muted hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 shrink-0"
                  data-testid="layers-parcels-expand"
                >
                  {parcelsExpanded
                    ? <ChevronDown className="w-3 h-3" aria-hidden />
                    : <ChevronRight className="w-3 h-3" aria-hidden />}
                </button>
                <span className="flex-1 type-body uppercase tracking-wider font-bold text-text-primary">
                  Parcels
                </span>
                <span className="type-meta text-text-muted">
                  {parcelList.filter(p => !hiddenParcelIds?.has(p.id)).length}/{parcelList.length}
                </span>
              </div>
              {(parcelsExpanded || q.length > 0) && (
                <div className="pl-7 pr-2 pb-1.5">
                  {parcelList.map(parcel => {
                    const parcelHidden = hiddenParcelIds?.has(parcel.id) ?? false;
                    return (
                      <label
                        key={parcel.id}
                        className="flex items-center gap-2 py-0.5 cursor-pointer hover:bg-state-hover"
                        data-testid={`parcel-row-${parcel.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={!parcelHidden}
                          onChange={() => onToggleParcel?.(parcel.id)}
                          className="w-3 h-3 accent-primary shrink-0"
                          data-testid={`parcel-toggle-${parcel.id}`}
                          aria-label={`Toggle parcel ${parcel.name}`}
                        />
                        <span
                          className="w-2 h-2 inline-block shrink-0 rounded-full"
                          style={{ backgroundColor: parcel.color }}
                          aria-hidden
                        />
                        <span
                          className={`flex-1 text-[10px] truncate ${parcelHidden ? 'text-text-muted line-through' : 'text-text-primary'}`}
                          title={parcel.name}
                        >
                          {parcel.name}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {/* Generation layout — publication-managed scenario layers under
              one header (scenario name + publish date), with a deep link
              back to the Generation & Impact tab and an optional draft
              (promoted-but-unpublished) preview toggle. */}
          {(managedGenerationLayers.length > 0 || draftLayout) && (
            <div
              data-testid="layers-generation-group"
              className="border-b border-border-subtle last:border-b-0"
            >
              <div className="flex items-center gap-1 px-2 py-1.5 hover:bg-state-hover">
                <button
                  type="button"
                  onClick={() => setGenerationExpanded(e => !e)}
                  aria-expanded={generationExpanded}
                  aria-label="Toggle Generation layout group"
                  className="text-text-muted hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 shrink-0"
                  data-testid="layers-generation-expand"
                >
                  {generationExpanded
                    ? <ChevronDown className="w-3 h-3" aria-hidden />
                    : <ChevronRight className="w-3 h-3" aria-hidden />}
                </button>
                <span className="flex-1 type-body uppercase tracking-widest font-bold text-text-primary">
                  Generation layout
                </span>
                {onOpenGeneration && (
                  <button
                    type="button"
                    onClick={onOpenGeneration}
                    className="text-text-muted hover:text-text-primary shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
                    aria-label="Open in Generation & Impact"
                    title="Open in Generation & Impact"
                    data-testid="layers-generation-open"
                  >
                    <ExternalLink className="w-3 h-3" aria-hidden />
                  </button>
                )}
              </div>
              {generationHead && managedGenerationLayers.length > 0 && (
                <div
                  className="px-2 pb-1 -mt-1 type-meta text-text-muted truncate"
                  data-testid="layers-generation-sublabel"
                  title={`${generationHead.scenarioName} · published ${generationHead.publishedAt}`}
                >
                  {generationHead.scenarioName} · {generationHead.publishedAt.slice(0, 10)}
                </div>
              )}
              {(generationExpanded || q.length > 0) && (
                <div className="pl-7 pr-2 pb-1.5">
                  {draftLayout && !q && (
                    <label
                      className="flex items-center gap-2 py-0.5 cursor-pointer hover:bg-state-hover"
                      data-testid="layers-generation-draft-row"
                    >
                      <input
                        type="checkbox"
                        checked={draftLayout.enabled}
                        onChange={draftLayout.onToggle}
                        className="w-3 h-3 accent-primary shrink-0"
                        data-testid="layers-generation-draft-toggle"
                        aria-label="Toggle draft generation layout preview"
                      />
                      <span className="flex-1 text-[10px] text-text-primary">
                        Draft layout preview
                        {draftLayout.enabled && draftLayout.count !== null
                          ? ` · ${draftLayout.count} shapes`
                          : ''}
                      </span>
                    </label>
                  )}
                  {draftLayout?.enabled && draftLayout.error && (
                    <div
                      className="pb-1 text-[10px] text-text-muted"
                      data-testid="layers-generation-draft-error"
                    >
                      {draftLayout.error}
                    </div>
                  )}
                  {(q ? managedGenerationLayers.filter(l => layerMatchesQuery(l, q)) : managedGenerationLayers).map(layer => {
                    const typeHidden = hiddenGeometryTypes?.has(layer.geometry.type) ?? false;
                    const layerHidden = hiddenLayerIds.has(layer.id)
                      || hiddenCategories.has(layer.category)
                      || typeHidden
                      || layer.visible === false;
                    const dotColor = colorForCategory(layer.category, layer.color);
                    return (
                      <label
                        key={layer.id}
                        className="flex items-center gap-2 py-0.5 cursor-pointer hover:bg-state-hover"
                        data-testid={`layers-row-${layer.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={!layerHidden}
                          disabled={hiddenCategories.has(layer.category) || typeHidden}
                          onChange={() => onToggleLayer(layer.id)}
                          className="w-3 h-3 accent-primary shrink-0"
                          data-testid={`layers-toggle-${layer.id}`}
                          aria-label={`Toggle layer ${layer.name}`}
                        />
                        <span
                          className="w-2 h-2 inline-block shrink-0 rounded-full"
                          style={{ backgroundColor: dotColor }}
                          aria-hidden
                        />
                        <span
                          className={`flex-1 text-[10px] truncate ${layerHidden ? 'text-text-muted line-through' : 'text-text-primary'}`}
                          title={layer.name}
                        >
                          {layer.name}
                        </span>
                        <span
                          className="shrink-0 rounded border border-border-subtle px-1 text-[10px] uppercase tracking-wider text-text-muted"
                          data-testid={`layers-badge-${layer.id}`}
                        >
                          {SOURCE_BADGES[layer.source] ?? layer.source}
                        </span>
                        {onZoomToLayer && layer.geometry.coordinates.length > 0 && (
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); onZoomToLayer(layer); }}
                            className="text-text-muted hover:text-text-primary shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
                            aria-label={`Zoom to ${layer.name}`}
                            title="Zoom to this layer"
                            data-testid={`layers-zoom-${layer.id}`}
                          >
                            <Locate className="w-3 h-3" aria-hidden />
                          </button>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {groups.map(({ category, layers: allGroupLayers }) => {
            const groupLayers = q
              ? allGroupLayers.filter(l => layerMatchesQuery(l, q))
              : allGroupLayers;
            if (q && groupLayers.length === 0) return null;
            const expanded = q ? true : expandedCategories.has(category);
            const categoryHidden = hiddenCategories.has(category);
            const groupVisibleCount = groupLayers.filter(l =>
              l.visible !== false && !hiddenLayerIds.has(l.id) && !categoryHidden,
            ).length;
            return (
              <div key={category} data-testid={`layers-group-${category}`} className="border-b border-border-subtle last:border-b-0">
                <div className="flex items-center gap-1 px-2 py-1.5 hover:bg-state-hover">
                  <button
                    type="button"
                    onClick={() => toggleCategoryExpanded(category)}
                    aria-expanded={expanded}
                    aria-label={`Toggle ${categoryKeyLabel(category)} group`}
                    className="text-text-muted hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 shrink-0"
                    data-testid={`layers-group-expand-${category}`}
                  >
                    {expanded
                      ? <ChevronDown className="w-3 h-3" aria-hidden />
                      : <ChevronRight className="w-3 h-3" aria-hidden />}
                  </button>
                  <span
                    className="w-2.5 h-2.5 inline-block shrink-0"
                    style={{ backgroundColor: (LAYER_COLORS as Record<string, string>)[category]
                      ?? colorForCategory(groupLayers[0]!.category, groupLayers[0]!.color) }}
                    aria-hidden
                  />
                  <span className={`flex-1 type-body uppercase tracking-widest font-bold ${categoryHidden ? 'text-text-muted line-through' : 'text-text-primary'}`}>
                    {categoryKeyLabel(category)}
                  </span>
                  <span className="type-meta text-text-muted">{groupVisibleCount}/{groupLayers.length}</span>
                  {onSoloCategory && (
                    <button
                      type="button"
                      onClick={() => onSoloCategory(category)}
                      className="text-text-muted hover:text-text-primary shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
                      aria-label={`Solo ${categoryKeyLabel(category)} category`}
                      title="Show only this category (click again to restore)"
                      data-testid={`layers-group-solo-${category}`}
                    >
                      <Crosshair className="w-3 h-3" aria-hidden />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onToggleCategory(category)}
                    className="text-text-muted hover:text-text-primary shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
                    aria-label={`${categoryHidden ? 'Show' : 'Hide'} ${categoryKeyLabel(category)} category`}
                    data-testid={`layers-group-toggle-${category}`}
                  >
                    {categoryHidden
                      ? <EyeOff className="w-3 h-3" aria-hidden />
                      : <Eye className="w-3 h-3" aria-hidden />}
                  </button>
                </div>
                {expanded && (
                  <div className="pl-7 pr-2 pb-1.5">
                    {onSetCategoryOpacity && (
                      <label className="flex items-center gap-2 py-1" data-testid={`layers-opacity-row-${category}`}>
                        <span className="text-[9px] uppercase tracking-wider text-text-muted shrink-0">Fill</span>
                        <input
                          type="range"
                          min={0.05}
                          max={1}
                          step={0.05}
                          value={categoryOpacity?.[category] ?? 0.25}
                          onChange={(e) => onSetCategoryOpacity(category, Number(e.target.value))}
                          className="flex-1 h-1 accent-primary"
                          aria-label={`${categoryKeyLabel(category)} fill opacity`}
                          data-testid={`layers-opacity-${category}`}
                        />
                        <span className="text-[9px] text-text-muted shrink-0 w-7 text-right">
                          {Math.round((categoryOpacity?.[category] ?? 0.25) * 100)}%
                        </span>
                      </label>
                    )}
                    {bundleGroupLayers(groupLayers).map(row => {
                      const ctx: RowContext = {
                        hiddenLayerIds,
                        hiddenGeometryTypes,
                        categoryHidden,
                        onToggleLayer,
                        onZoomToLayer,
                      };
                      if (row.kind === 'bundle') {
                        const bundleKey = `${category}:${row.key}`;
                        return (
                          <BundleRow
                            key={bundleKey}
                            bundle={row}
                            ctx={ctx}
                            expanded={expandedBundles.has(bundleKey)}
                            onToggleExpanded={() => toggleBundleExpanded(bundleKey)}
                          />
                        );
                      }
                      const rowIndex = groupLayers.indexOf(row.layer);
                      return (
                        <LayerRow
                          key={row.layer.id}
                          layer={row.layer}
                          ctx={ctx}
                          reorder={onMoveLayer && groupLayers.length > 1
                            ? {
                                canUp: rowIndex > 0,
                                canDown: rowIndex < groupLayers.length - 1,
                                onMove: onMoveLayer,
                              }
                            : undefined}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {(hiddenCategories.size > 0 || hiddenLayerIds.size > 0 || (hiddenParcelIds?.size ?? 0) > 0 || (hiddenGeometryTypes?.size ?? 0) > 0) && (
            <button
              type="button"
              onClick={onReset}
              className="w-full type-body uppercase tracking-widest text-text-muted hover:text-text-primary px-3 py-1.5 border-t border-border-subtle hover:bg-state-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
              data-testid="layers-reset"
            >
              Reset all
            </button>
          )}
        </div>
      )}
    </div>
  );
}
